import { 
  collection, 
  doc, 
  addDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit,
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore';
import { db } from './config';

// Log types for different system events
export const LOG_TYPES = {
  USER_CREATED: 'user_created',
  USER_UPDATED: 'user_updated',
  USER_DELETED: 'user_deleted',
  USER_LOGIN: 'user_login',
  USER_LOGOUT: 'user_logout',
  
  APARTMENT_CREATED: 'apartment_created',
  APARTMENT_UPDATED: 'apartment_updated',
  APARTMENT_DELETED: 'apartment_deleted',
  APARTMENT_OCCUPIED: 'apartment_occupied',
  APARTMENT_VACATED: 'apartment_vacated',
  
  PAYMENT_CREATED: 'payment_created',
  PAYMENT_UPDATED: 'payment_updated',
  PAYMENT_DELETED: 'payment_deleted',
  PAYMENT_STATUS_CHANGED: 'payment_status_changed',
  PAYMENT_OVERDUE: 'payment_overdue',
  LATE_FEE_APPLIED: 'late_fee_applied',
  
  MAINTENANCE_CREATED: 'maintenance_created',
  MAINTENANCE_UPDATED: 'maintenance_updated',
  MAINTENANCE_DELETED: 'maintenance_deleted',
  MAINTENANCE_STATUS_CHANGED: 'maintenance_status_changed',
  MAINTENANCE_COMPLETED: 'maintenance_completed',
  
  NOTICE_CREATED: 'notice_created',
  NOTICE_UPDATED: 'notice_updated',
  NOTICE_DELETED: 'notice_deleted',
  
  MESSAGE_SENT: 'message_sent',
  MESSAGE_DELETED: 'message_deleted',
  
  NOTIFICATION_CREATED: 'notification_created',
  NOTIFICATION_READ: 'notification_read',
  
  BACKUP_CREATED: 'backup_created',
  BACKUP_DELETED: 'backup_deleted',
  RESTORE_PERFORMED: 'restore_performed',
  DATA_CLEARED: 'data_cleared',
  
  SYSTEM_HEALTH_CHECK: 'system_health_check',
  SYSTEM_ERROR: 'system_error',
  SYSTEM_WARNING: 'system_warning'
};

// Log priorities
export const LOG_PRIORITIES = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical'
};

// Create a new log entry
export const createLog = async (logData) => {
  try {
    const log = {
      ...logData,
      timestamp: serverTimestamp(),
      id: null // Will be set by Firestore
    };

    const docRef = await addDoc(collection(db, 'logs'), log);
    
    return { 
      success: true, 
      logId: docRef.id,
      log: {
        id: docRef.id,
        ...log
      }
    };
  } catch (error) {
    console.error('Error creating log:', error);
    return { success: false, error: error.message };
  }
};

// Get logs with filtering options
export const getLogs = async (options = {}) => {
  try {
    const {
      limit: logLimit = 100,
      type = null,
      priority = null,
      userId = null,
      startDate = null,
      endDate = null
    } = options;

    let q = query(
      collection(db, 'logs'),
      orderBy('timestamp', 'desc')
    );

    // Apply filters
    if (type) {
      q = query(q, where('type', '==', type));
    }
    if (priority) {
      q = query(q, where('priority', '==', priority));
    }
    if (userId) {
      q = query(q, where('userId', '==', userId));
    }

    q = query(q, limit(logLimit));
    
    const querySnapshot = await getDocs(q);
    const logs = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Apply date filtering if needed
    let filteredLogs = logs;
    if (startDate || endDate) {
      filteredLogs = logs.filter(log => {
        const logDate = log.timestamp?.seconds ? new Date(log.timestamp.seconds * 1000) : new Date(log.timestamp);
        if (startDate && logDate < new Date(startDate)) return false;
        if (endDate && logDate > new Date(endDate)) return false;
        return true;
      });
    }

    return { success: true, logs: filteredLogs };
  } catch (error) {
    console.error('Error fetching logs:', error);
    return { success: false, error: error.message };
  }
};

// Real-time listener for logs
export const subscribeToLogs = (callback, options = {}) => {
  const {
    limit: logLimit = 50,
    type = null,
    priority = null
  } = options;

  let q = query(
    collection(db, 'logs'),
    orderBy('timestamp', 'desc')
  );

  if (type) {
    q = query(q, where('type', '==', type));
  }
  if (priority) {
    q = query(q, where('priority', '==', priority));
  }

  q = query(q, limit(logLimit));
  
  return onSnapshot(q, (snapshot) => {
    const logs = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    callback(logs);
  });
};

// Log creation helpers for different types
export const logHelpers = {
  // User-related logs
  createUserLog: async (type, userData, performedBy = 'system') => {
    let message, priority;
    
    switch (type) {
      case LOG_TYPES.USER_CREATED:
        message = `New user account created: ${userData.email || userData.uid}`;
        priority = LOG_PRIORITIES.MEDIUM;
        break;
      case LOG_TYPES.USER_UPDATED:
        message = `User account updated: ${userData.email || userData.uid}`;
        priority = LOG_PRIORITIES.LOW;
        break;
      case LOG_TYPES.USER_DELETED:
        message = `User account deleted: ${userData.email || userData.uid}`;
        priority = LOG_PRIORITIES.HIGH;
        break;
      case LOG_TYPES.USER_LOGIN:
        message = `User logged in: ${userData.email || userData.uid}`;
        priority = LOG_PRIORITIES.LOW;
        break;
      case LOG_TYPES.USER_LOGOUT:
        message = `User logged out: ${userData.email || userData.uid}`;
        priority = LOG_PRIORITIES.LOW;
        break;
      default:
        message = `User action performed: ${userData.email || userData.uid}`;
        priority = LOG_PRIORITIES.MEDIUM;
    }

    return createLog({
      type,
      message,
      priority,
      userId: userData.uid,
      performedBy,
      data: {
        userEmail: userData.email,
        userRole: userData.role,
        action: type
      }
    });
  },

  // Apartment-related logs
  createApartmentLog: async (type, apartmentData, performedBy = 'system') => {
    let message, priority;
    
    switch (type) {
      case LOG_TYPES.APARTMENT_CREATED:
        message = `New apartment created: ${apartmentData.apartmentNumber || apartmentData.id}`;
        priority = LOG_PRIORITIES.MEDIUM;
        break;
      case LOG_TYPES.APARTMENT_UPDATED:
        message = `Apartment updated: ${apartmentData.apartmentNumber || apartmentData.id}`;
        priority = LOG_PRIORITIES.LOW;
        break;
      case LOG_TYPES.APARTMENT_DELETED:
        message = `Apartment deleted: ${apartmentData.apartmentNumber || apartmentData.id}`;
        priority = LOG_PRIORITIES.HIGH;
        break;
      case LOG_TYPES.APARTMENT_OCCUPIED:
        message = `Apartment occupied: ${apartmentData.apartmentNumber || apartmentData.id}`;
        priority = LOG_PRIORITIES.MEDIUM;
        break;
      case LOG_TYPES.APARTMENT_VACATED:
        message = `Apartment vacated: ${apartmentData.apartmentNumber || apartmentData.id}`;
        priority = LOG_PRIORITIES.MEDIUM;
        break;
      default:
        message = `Apartment action performed: ${apartmentData.apartmentNumber || apartmentData.id}`;
        priority = LOG_PRIORITIES.MEDIUM;
    }

    return createLog({
      type,
      message,
      priority,
      performedBy,
      data: {
        apartmentId: apartmentData.id,
        apartmentNumber: apartmentData.apartmentNumber,
        building: apartmentData.building,
        floor: apartmentData.floor,
        status: apartmentData.status,
        tenantId: apartmentData.tenantId,
        action: type
      }
    });
  },

  // Payment-related logs
  createPaymentLog: async (type, paymentData, performedBy = 'system') => {
    let message, priority;
    
    switch (type) {
      case LOG_TYPES.PAYMENT_CREATED:
        message = `New payment created: ₱${paymentData.amount} for ${paymentData.tenantName || 'tenant'}`;
        priority = LOG_PRIORITIES.MEDIUM;
        break;
      case LOG_TYPES.PAYMENT_UPDATED:
        message = `Payment updated: ₱${paymentData.amount} for ${paymentData.tenantName || 'tenant'}`;
        priority = LOG_PRIORITIES.LOW;
        break;
      case LOG_TYPES.PAYMENT_DELETED:
        message = `Payment deleted: ₱${paymentData.amount} for ${paymentData.tenantName || 'tenant'}`;
        priority = LOG_PRIORITIES.HIGH;
        break;
      case LOG_TYPES.PAYMENT_STATUS_CHANGED:
        message = `Payment status changed to ${paymentData.status}: ₱${paymentData.amount} for ${paymentData.tenantName || 'tenant'}`;
        priority = LOG_PRIORITIES.MEDIUM;
        break;
      case LOG_TYPES.PAYMENT_OVERDUE:
        message = `Payment overdue: ₱${paymentData.amount} for ${paymentData.tenantName || 'tenant'}`;
        priority = LOG_PRIORITIES.HIGH;
        break;
      case LOG_TYPES.LATE_FEE_APPLIED:
        message = `Late fee applied: ₱${paymentData.amount} for ${paymentData.tenantName || 'tenant'}`;
        priority = LOG_PRIORITIES.HIGH;
        break;
      default:
        message = `Payment action performed: ₱${paymentData.amount} for ${paymentData.tenantName || 'tenant'}`;
        priority = LOG_PRIORITIES.MEDIUM;
    }

    return createLog({
      type,
      message,
      priority,
      performedBy,
      data: {
        paymentId: paymentData.id,
        amount: paymentData.amount,
        status: paymentData.status,
        tenantName: paymentData.tenantName,
        action: type
      }
    });
  },

  // Maintenance-related logs
  createMaintenanceLog: async (type, maintenanceData, performedBy = 'system') => {
    let message, priority;
    
    switch (type) {
      case LOG_TYPES.MAINTENANCE_CREATED:
        message = `New maintenance request: ${maintenanceData.issueType || 'Unknown'} for apartment ${maintenanceData.apartmentId || 'Unknown'}`;
        priority = LOG_PRIORITIES.MEDIUM;
        break;
      case LOG_TYPES.MAINTENANCE_UPDATED:
        message = `Maintenance request updated: ${maintenanceData.issueType || 'Unknown'} for apartment ${maintenanceData.apartmentId || 'Unknown'}`;
        priority = LOG_PRIORITIES.LOW;
        break;
      case LOG_TYPES.MAINTENANCE_DELETED:
        message = `Maintenance request deleted: ${maintenanceData.issueType || 'Unknown'} for apartment ${maintenanceData.apartmentId || 'Unknown'}`;
        priority = LOG_PRIORITIES.HIGH;
        break;
      case LOG_TYPES.MAINTENANCE_STATUS_CHANGED:
        message = `Maintenance status changed to ${maintenanceData.status}: ${maintenanceData.issueType || 'Unknown'} for apartment ${maintenanceData.apartmentId || 'Unknown'}`;
        priority = LOG_PRIORITIES.MEDIUM;
        break;
      case LOG_TYPES.MAINTENANCE_COMPLETED:
        message = `Maintenance completed: ${maintenanceData.issueType || 'Unknown'} for apartment ${maintenanceData.apartmentId || 'Unknown'}`;
        priority = LOG_PRIORITIES.MEDIUM;
        break;
      default:
        message = `Maintenance action performed: ${maintenanceData.issueType || 'Unknown'} for apartment ${maintenanceData.apartmentId || 'Unknown'}`;
        priority = LOG_PRIORITIES.MEDIUM;
    }

    return createLog({
      type,
      message,
      priority,
      performedBy,
      data: {
        maintenanceId: maintenanceData.id,
        issueType: maintenanceData.issueType,
        status: maintenanceData.status,
        apartmentId: maintenanceData.apartmentId,
        requestedBy: maintenanceData.requestedBy,
        issue: maintenanceData.issue,
        action: type
      }
    });
  },

  // System-related logs
  createSystemLog: async (type, systemData, performedBy = 'system') => {
    let message, priority;
    
    switch (type) {
      case LOG_TYPES.BACKUP_CREATED:
        message = `System backup created: ${systemData.name || 'Backup'} (${systemData.documentCount || 0} documents)`;
        priority = LOG_PRIORITIES.MEDIUM;
        break;
      case LOG_TYPES.BACKUP_DELETED:
        message = `System backup deleted: ${systemData.name || 'Backup'}`;
        priority = LOG_PRIORITIES.HIGH;
        break;
      case LOG_TYPES.RESTORE_PERFORMED:
        message = `System restore performed: ${systemData.totalRestored || 0} documents restored from ${systemData.backupName || 'backup'}`;
        priority = LOG_PRIORITIES.HIGH;
        break;
      case LOG_TYPES.DATA_CLEARED:
        message = `Data cleared: ${systemData.totalCleared || 0} documents from ${systemData.collections?.length || 0} collections`;
        priority = LOG_PRIORITIES.CRITICAL;
        break;
      case LOG_TYPES.SYSTEM_HEALTH_CHECK:
        message = `System health check: ${systemData.healthScore || 0}% health score`;
        priority = LOG_PRIORITIES.LOW;
        break;
      case LOG_TYPES.SYSTEM_ERROR:
        message = `System error: ${systemData.error || 'Unknown error'}`;
        priority = LOG_PRIORITIES.CRITICAL;
        break;
      case LOG_TYPES.SYSTEM_WARNING:
        message = `System warning: ${systemData.warning || 'Unknown warning'}`;
        priority = LOG_PRIORITIES.HIGH;
        break;
      default:
        message = `System action performed: ${type}`;
        priority = LOG_PRIORITIES.MEDIUM;
    }

    return createLog({
      type,
      message,
      priority,
      performedBy,
      data: {
        ...systemData,
        action: type
      }
    });
  }
};

// Auto-create logs for database changes
export const autoCreateLogs = {
  // Called when a user is created, updated, or deleted
  onUserChange: async (userData, action, performedBy = 'system') => {
    let logType;
    switch (action) {
      case 'created':
        logType = LOG_TYPES.USER_CREATED;
        break;
      case 'updated':
        logType = LOG_TYPES.USER_UPDATED;
        break;
      case 'deleted':
        logType = LOG_TYPES.USER_DELETED;
        break;
      case 'login':
        logType = LOG_TYPES.USER_LOGIN;
        break;
      case 'logout':
        logType = LOG_TYPES.USER_LOGOUT;
        break;
      default:
        return;
    }
    
    return logHelpers.createUserLog(logType, userData, performedBy);
  },

  // Called when an apartment is created, updated, or deleted
  onApartmentChange: async (apartmentData, action, performedBy = 'system') => {
    let logType;
    switch (action) {
      case 'created':
        logType = LOG_TYPES.APARTMENT_CREATED;
        break;
      case 'updated':
        logType = LOG_TYPES.APARTMENT_UPDATED;
        break;
      case 'deleted':
        logType = LOG_TYPES.APARTMENT_DELETED;
        break;
      case 'occupied':
        logType = LOG_TYPES.APARTMENT_OCCUPIED;
        break;
      case 'vacated':
        logType = LOG_TYPES.APARTMENT_VACATED;
        break;
      default:
        return;
    }
    
    return logHelpers.createApartmentLog(logType, apartmentData, performedBy);
  },

  // Called when a payment is created, updated, or status changes
  onPaymentChange: async (paymentData, action, performedBy = 'system') => {
    let logType;
    switch (action) {
      case 'created':
        logType = LOG_TYPES.PAYMENT_CREATED;
        break;
      case 'updated':
        logType = LOG_TYPES.PAYMENT_UPDATED;
        break;
      case 'deleted':
        logType = LOG_TYPES.PAYMENT_DELETED;
        break;
      case 'status_changed':
        logType = LOG_TYPES.PAYMENT_STATUS_CHANGED;
        break;
      case 'overdue':
        logType = LOG_TYPES.PAYMENT_OVERDUE;
        break;
      case 'late_fee':
        logType = LOG_TYPES.LATE_FEE_APPLIED;
        break;
      default:
        return;
    }
    
    return logHelpers.createPaymentLog(logType, paymentData, performedBy);
  },

  // Called when maintenance is created, updated, or status changes
  onMaintenanceChange: async (maintenanceData, action, performedBy = 'system') => {
    let logType;
    switch (action) {
      case 'created':
        logType = LOG_TYPES.MAINTENANCE_CREATED;
        break;
      case 'updated':
        logType = LOG_TYPES.MAINTENANCE_UPDATED;
        break;
      case 'deleted':
        logType = LOG_TYPES.MAINTENANCE_DELETED;
        break;
      case 'status_changed':
        logType = LOG_TYPES.MAINTENANCE_STATUS_CHANGED;
        break;
      case 'completed':
        logType = LOG_TYPES.MAINTENANCE_COMPLETED;
        break;
      default:
        return;
    }
    
    return logHelpers.createMaintenanceLog(logType, maintenanceData, performedBy);
  },

  // Called when system operations are performed
  onSystemOperation: async (systemData, action, performedBy = 'system') => {
    let logType;
    switch (action) {
      case 'backup_created':
        logType = LOG_TYPES.BACKUP_CREATED;
        break;
      case 'backup_deleted':
        logType = LOG_TYPES.BACKUP_DELETED;
        break;
      case 'restore_performed':
        logType = LOG_TYPES.RESTORE_PERFORMED;
        break;
      case 'data_cleared':
        logType = LOG_TYPES.DATA_CLEARED;
        break;
      case 'health_check':
        logType = LOG_TYPES.SYSTEM_HEALTH_CHECK;
        break;
      case 'error':
        logType = LOG_TYPES.SYSTEM_ERROR;
        break;
      case 'warning':
        logType = LOG_TYPES.SYSTEM_WARNING;
        break;
      default:
        return;
    }
    
    return logHelpers.createSystemLog(logType, systemData, performedBy);
  },

  // Helper function to log system errors
  logSystemError: async (error, context = 'system', performedBy = 'system') => {
    return logHelpers.createSystemLog(LOG_TYPES.SYSTEM_ERROR, {
      error: error.message || error,
      context,
      stack: error.stack,
      timestamp: new Date().toISOString()
    }, performedBy);
  },

  // Helper function to log system warnings
  logSystemWarning: async (warning, context = 'system', performedBy = 'system') => {
    return logHelpers.createSystemLog(LOG_TYPES.SYSTEM_WARNING, {
      warning: warning.message || warning,
      context,
      timestamp: new Date().toISOString()
    }, performedBy);
  }
}; 